import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServerSearch } from '@/lib/useServerSearch';
import {
  Plus,
  MagnifyingGlass as Search,
  ListChecks,
  CircleNotch,
  Warning,
  Scales,
  CheckCircle,
  LockKey,
  CaretRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RowActions } from '@/components/shared/RowActions';
import { TaskModal } from '@/components/tasks/TaskModal';
import { TaskDetailPanel } from '@/components/tasks/TaskDetailPanel';
import { CompleteTaskDialog } from '@/components/tasks/CompleteTaskDialog';
import {
  fetchTasks,
  updateTask,
  deleteTask,
  PRIORITY_LABELS,
  STATUS_LABELS,
  type Task,
  type TaskStatus,
} from '@/lib/tasksApi';
import { uploadDocument as uploadDocumentApi } from '@/lib/documentsApi';
import { getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { useChambersMode } from '@/lib/useChambersMode';
import { useClerkWorkspaceStore } from '@/lib/clerkWorkspaceStore';

type Tab = 'outstanding' | 'completed';

const OPEN_STATUSES: TaskStatus[] = ['pending', 'in_progress', 'blocked', 'awaiting_barrister', 'awaiting_clerk', 'hearing_followup', 'filing_pending'];

function fmtDue(iso: string | null): { label: string; isOverdue: boolean } {
  if (!iso) return { label: '—', isOverdue: false };
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  let label: string;
  if (days === 0) label = 'Today';
  else if (days === 1) label = 'Tomorrow';
  else if (days === -1) label = 'Yesterday';
  else if (days < 0) label = `${Math.abs(days)}d overdue`;
  else if (days <= 7) label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  else label = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return { label, isOverdue: days < 0 };
}

/*
 * A hundred tasks in one flat list is a scroll, not a worklist: the three that are overdue sit
 * in the same undifferentiated run as the forty with no due date at all. Tasks are grouped into
 * date bands instead, each one collapsible, with the bands you rarely act on shut by default.
 */
interface TaskBand {
  id: string;
  label: string;
  defaultOpen: boolean;
}

const OUTSTANDING_BANDS: TaskBand[] = [
  { id: 'overdue', label: 'Overdue', defaultOpen: true },
  { id: 'today', label: 'Today', defaultOpen: true },
  { id: 'tomorrow', label: 'Tomorrow', defaultOpen: true },
  { id: 'week', label: 'This week', defaultOpen: true },
  { id: 'later', label: 'Later', defaultOpen: false },
  { id: 'undated', label: 'No due date', defaultOpen: false },
];

const COMPLETED_BANDS: TaskBand[] = [
  { id: 'done-today', label: 'Today', defaultOpen: true },
  { id: 'done-yesterday', label: 'Yesterday', defaultOpen: true },
  { id: 'done-week', label: 'Earlier this week', defaultOpen: true },
  { id: 'done-older', label: 'Older', defaultOpen: false },
];

/** Whole days from today to `iso`; negative is in the past. */
function dayOffset(iso: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(iso); target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

function outstandingBand(dueDate: string | null): string {
  if (!dueDate) return 'undated';
  const days = dayOffset(dueDate);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days <= 7) return 'week';
  return 'later';
}

function completedBand(completedAt: string): string {
  const days = -dayOffset(completedAt);
  if (days <= 0) return 'done-today';
  if (days === 1) return 'done-yesterday';
  if (days <= 7) return 'done-week';
  return 'done-older';
}

function priorityBadge(p: string): string {
  const map: Record<string, string> = {
    urgent: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30',
    high:   'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30',
    normal: 'hidden',
    low:    'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30',
  };
  return map[p] ?? '';
}

function statusBadge(s: TaskStatus): string {
  const map: Record<TaskStatus, string> = {
    completed:          'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30',
    in_progress:        'bg-brand-50 text-brand-700 dark:text-brand-300 border border-brand-200',
    blocked:            'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30',
    cancelled:          'bg-zinc-100 text-zinc-500 border border-zinc-200',
    awaiting_barrister: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30',
    awaiting_clerk:     'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30',
    hearing_followup:   'bg-purple-50 text-purple-700 dark:text-purple-300 border border-purple-200',
    filing_pending:     'bg-purple-50 text-purple-700 dark:text-purple-300 border border-purple-200',
    pending:            'bg-zinc-100 text-zinc-600 dark:text-zinc-400 border border-zinc-200',
  };
  return map[s];
}

function statusLeftBorder(s: TaskStatus): string {
  switch (s) {
    case 'blocked':                              return 'border-l-[3px] border-l-red-400';
    case 'in_progress':                          return 'border-l-[3px] border-l-brand-500';
    case 'awaiting_barrister':
    case 'awaiting_clerk':                       return 'border-l-[3px] border-l-amber-400';
    case 'hearing_followup':
    case 'filing_pending':                       return 'border-l-[3px] border-l-purple-400';
    default:                                     return 'border-l-[3px] border-l-transparent';
  }
}

export default function TasksPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useAuthStore((s) => s.user);
  const { isSolo } = useChambersMode();
  const caseIdFromUrl = searchParams.get('caseId') ?? undefined;

  const [tab, setTab] = useState<Tab>('outstanding');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Task | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [completeTarget, setCompleteTarget] = useState<Task | null>(null);

  const tasksQ = useQuery({
    queryKey: ['tasks', { tab }],
    queryFn: () => fetchTasks({ limit: 100, sortBy: 'dueDate', sortOrder: 'asc' }),
  });

  // Server-side search finds tasks beyond the 100-row window; stats stay driven by
  // tasksQ, the list swaps to server results while searching. Canonical hook (H3).
  const { isSearching, results: searchResults } = useServerSearch(search, {
    key: ['tasks-search'],
    fetcher: (q) => fetchTasks({ search: q, limit: 50, sortBy: 'dueDate', sortOrder: 'asc' }).then((r) => r.tasks),
  });

  const visibleTasks = useMemo(() => {
    const all = (isSearching ? searchResults : tasksQ.data?.tasks) ?? [];
    const filtered = all.filter((t) =>
      tab === 'completed' ? t.status === 'completed' : OPEN_STATUSES.includes(t.status),
    );
    if (!search.trim() || isSearching) return filtered; // server already matched when searching
    const q = search.toLowerCase();
    return filtered.filter((t) =>
      [t.title, t.description ?? '', t.case?.caseNumber ?? '', t.case?.caseName ?? '',
        t.assignedTo ? `${t.assignedTo.firstName} ${t.assignedTo.lastName}` : '']
        .join(' ').toLowerCase().includes(q),
    );
  }, [tasksQ.data, searchResults, isSearching, tab, search]);

  const totals = useMemo(() => {
    const all = tasksQ.data?.tasks ?? [];
    const open = all.filter((t) => OPEN_STATUSES.includes(t.status));
    const high = open.filter((t) => t.priority === 'high' || t.priority === 'urgent');
    const completedThisWeek = all.filter((t) => {
      if (t.status !== 'completed' || !t.completedAt) return false;
      return (Date.now() - new Date(t.completedAt).getTime()) / 86_400_000 <= 7;
    });
    const sevenDays = Date.now() + 7 * 86_400_000;
    const dueSoon = open.filter((t) => t.dueDate && new Date(t.dueDate).getTime() <= sevenDays);
    return { open: open.length, high: high.length, completedThisWeek: completedThisWeek.length, dueSoon: dueSoon.length };
  }, [tasksQ.data]);

  const [bandOverride, setBandOverride] = useState<Record<string, boolean>>({});
  const searching = search.trim().length > 0;

  // While searching every band is forced open: a match must never hide inside a collapsed band.
  const bandIsOpen = (band: TaskBand) => searching || (bandOverride[band.id] ?? band.defaultOpen);
  const toggleBand = (band: TaskBand) =>
    setBandOverride((prev) => ({ ...prev, [band.id]: !(prev[band.id] ?? band.defaultOpen) }));

  const bands = useMemo(() => {
    const defs = tab === 'completed' ? COMPLETED_BANDS : OUTSTANDING_BANDS;
    const byBand = new Map<string, Task[]>();
    for (const t of visibleTasks) {
      const id = tab === 'completed'
        ? completedBand(t.completedAt ?? t.updatedAt)
        : outstandingBand(t.dueDate);
      const existing = byBand.get(id);
      if (existing) existing.push(t);
      else byBand.set(id, [t]);
    }
    return defs
      .map((def) => ({ ...def, tasks: byBand.get(def.id) ?? [] }))
      .filter((band) => band.tasks.length > 0);
  }, [visibleTasks, tab]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['matter-tasks'] });
    if (caseIdFromUrl) qc.invalidateQueries({ queryKey: ['matter-tasks', caseIdFromUrl] });
  };

  const completeMut = useMutation({
    mutationFn: async ({ id, note, file, caseId }: { id: string; note: string; file?: File; caseId?: string }) => {
      await updateTask(id, { status: 'completed', completionNote: note });
      if (file && caseId) {
        await uploadDocumentApi(file, { caseId, category: 'task_completion' });
      }
    },
    onSuccess: () => {
      toast.success('Task completion recorded');
      setCompleteTarget(null);
      setDetailTask(null);
      invalidateAll();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not record completion')),
  });

  const reopenMut = useMutation({
    mutationFn: (id: string) => updateTask(id, { status: 'pending' }),
    onSuccess: () => { toast.success('Task reopened'); setDetailTask(null); invalidateAll(); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not reopen task')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => { toast.success('Task deleted'); setDetailTask(null); invalidateAll(); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete task')),
  });

  const hasManageTasks = useClerkWorkspaceStore((s) => s.hasPermission('MANAGE_TASKS'));

  const canEdit = (t: Task) =>
    t.assignedToId === currentUser?.id ||
    t.createdById === currentUser?.id ||
    currentUser?.memberType === 'admin' ||
    currentUser?.memberType === 'staff' ||
    hasManageTasks;

  const basePath = currentUser?.memberType === 'senior_clerk' || currentUser?.memberType === 'junior_clerk'
    ? '/clerk' : '/admin';

  const renderOutstandingRow = (t: Task) => {
    const due = fmtDue(t.dueDate);
    return (
      <li
        key={t.id}
        className={`group cursor-pointer transition hover:bg-muted/20 ${statusLeftBorder(t.status)}`}
        onClick={() => setDetailTask(t)}
      >
        <div className="flex items-start gap-3 px-5 py-2.5">
          <div className="min-w-0 flex-1">
            {/* Title row */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {t.isPrivate && <LockKey size={11} weight="fill" className="text-muted-foreground/40 shrink-0" />}
              <span className="text-sm font-medium text-foreground">{t.title}</span>
              {t.priority !== 'normal' && (
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${priorityBadge(t.priority)}`}>
                  {PRIORITY_LABELS[t.priority as keyof typeof PRIORITY_LABELS]}
                </span>
              )}
              {t.status !== 'pending' && (
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusBadge(t.status)}`}>
                  {STATUS_LABELS[t.status]}
                </span>
              )}
            </div>

            {/* Meta row */}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0 text-xs text-muted-foreground/70">
              {t.case && (
                <span className="flex items-center gap-1">
                  <Scales size={10} weight="duotone" />
                  {t.case.caseNumber} · {t.case.caseName}
                </span>
              )}
              {!isSolo && t.assignedTo && (
                <span>{t.assignedTo.firstName} {t.assignedTo.lastName}</span>
              )}
              {!isSolo && !t.assignedTo && (
                <span className="text-muted-foreground/40">Unassigned</span>
              )}
              <span className={due.isOverdue ? 'flex items-center gap-0.5 font-medium text-red-600 dark:text-red-400' : ''}>
                {due.isOverdue && <Warning size={10} weight="fill" />}
                {due.label}
              </span>
              {t.hearing && (
                <span>
                  {t.hearing.hearingType} ·{' '}
                  {new Date(t.hearing.hearingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="Complete"
              aria-label="Complete"
              disabled={!canEdit(t)}
              onClick={(e) => { e.stopPropagation(); setCompleteTarget(t); }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-emerald-50 dark:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:pointer-events-none"
            >
              <CheckCircle className="h-4 w-4" />
            </button>
            <RowActions
              onView={() => setDetailTask(t)}
              onEdit={canEdit(t) ? () => setEditTarget(t) : undefined}
              onDelete={canEdit(t) ? () => deleteMut.mutate(t.id) : undefined}
              deleteConfirm={{
                title: 'Delete task?',
                message: `Delete “${t.title}”? This removes it from the matter.`,
                confirmLabel: 'Delete',
              }}
            />
          </div>
        </div>
      </li>
    );
  };

  const renderCompletedRow = (t: Task) => {
    const completedTs = t.completedAt ?? t.updatedAt;
    return (
      <li
        key={t.id}
        className="group cursor-pointer border-l-[3px] border-l-emerald-400 transition hover:bg-muted/10"
        onClick={() => setDetailTask(t)}
      >
        <div className="flex items-start gap-3 px-5 py-2.5">
          <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {t.isPrivate && <LockKey size={11} weight="fill" className="text-muted-foreground/30 shrink-0" />}
              <span className="text-sm text-foreground/75">{t.title}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground/60">
              <span>
                Completed {new Date(completedTs).toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </span>
              {t.completedBy && (
                <>
                  <span className="text-muted-foreground/30">·</span>
                  <span>{t.completedBy.firstName} {t.completedBy.lastName}</span>
                </>
              )}
              {t.case && (
                <>
                  <span className="text-muted-foreground/30">·</span>
                  <span className="flex items-center gap-1">
                    <Scales size={10} weight="duotone" />
                    {t.case.caseNumber}
                  </span>
                </>
              )}
            </div>
            {t.completionNote && (
              <div className="mt-1.5 rounded border-l-2 border-emerald-200 dark:border-emerald-500/30 pl-2.5 text-xs text-muted-foreground/70 italic">
                {t.completionNote}
              </div>
            )}
          </div>
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <RowActions
              onView={() => setDetailTask(t)}
              onDelete={canEdit(t) ? () => deleteMut.mutate(t.id) : undefined}
              deleteConfirm={{
                title: 'Delete task?',
                message: `Delete “${t.title}”? This removes it from the matter.`,
                confirmLabel: 'Delete',
              }}
            />
          </div>
        </div>
      </li>
    );
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Tasks"
        subtitle={`${totals.open} outstanding · ${totals.dueSoon} due this week · ${totals.high} high priority`}
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
            New task
          </Button>
        }
      />

      {/* Summary bar */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Outstanding', value: totals.open, note: `${totals.dueSoon} due this week` },
          { label: 'High priority', value: totals.high, note: 'urgent or high' },
          { label: 'Completed this week', value: totals.completedThisWeek, note: '' },
          { label: 'Due this week', value: totals.dueSoon, note: '' },
        ].map((s) => (
          <div key={s.label} className="rounded-md border border-border bg-card px-4 py-3">
            <div className="text-xs text-muted-foreground uppercase tracking-[0.06em]">{s.label}</div>
            <div className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">{s.value}</div>
            {s.note && <div className="mt-0.5 text-[11px] text-muted-foreground/70">{s.note}</div>}
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 border-b border-border">
        {([
          { key: 'outstanding' as const, label: 'Outstanding', count: totals.open },
          { key: 'completed' as const,   label: 'Completed',   count: totals.completedThisWeek },
        ]).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <Card className="mb-4 p-3">
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            placeholder="Filter by task, matter, assignee…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
      </Card>

      {/* Task list */}
      <Card className="overflow-hidden">
        {tasksQ.isLoading && (
          <div className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
            <CircleNotch size={14} className="animate-spin" /> Loading tasks…
          </div>
        )}
        {tasksQ.isError && (
          <div className="px-5 py-6 text-sm text-red-700 dark:text-red-300">
            {getApiErrorMessage(tasksQ.error, 'Could not load tasks.')}
          </div>
        )}
        {tasksQ.data && visibleTasks.length === 0 && (
          <div className="px-5 py-12 text-center">
            <ListChecks className="mx-auto mb-3 h-8 w-8 text-muted-foreground" weight="duotone" />
            <div className="mb-1 text-sm font-medium">
              {search ? 'No tasks match your search' : tab === 'completed' ? 'No completed tasks yet' : 'No outstanding tasks'}
            </div>
            <p className="mx-auto max-w-md text-xs text-muted-foreground">
              {search ? 'Try a different keyword or clear the search.' : 'Create one above to get started.'}
            </p>
          </div>
        )}

        {visibleTasks.length > 0 && (
          <div className="divide-y divide-border">
            {bands.map((band) => {
              const open = bandIsOpen(band);
              return (
                <div key={band.id}>
                  <button
                    type="button"
                    onClick={() => toggleBand(band)}
                    aria-expanded={open}
                    className="flex w-full items-center gap-2 bg-muted/30 px-5 py-2 text-left transition hover:bg-muted/50"
                  >
                    <CaretRight
                      size={11}
                      weight="bold"
                      className={`shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
                    />
                    <span className={`text-xs font-semibold uppercase tracking-wider ${band.id === 'overdue' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                      {band.label}
                    </span>
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                      {band.tasks.length}
                    </span>
                  </button>
                  {open && (
                    <ul className="divide-y divide-border">
                      {band.tasks.map((t) => (tab === 'completed' ? renderCompletedRow(t) : renderOutstandingRow(t)))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Task detail panel */}
      {detailTask && (
        <TaskDetailPanel
          task={detailTask}
          isSolo={isSolo}
          canEdit={canEdit(detailTask)}
          onClose={() => setDetailTask(null)}
          onEdit={() => { setEditTarget(detailTask); setDetailTask(null); }}
          onComplete={() => setCompleteTarget(detailTask)}
          onReopen={() => reopenMut.mutate(detailTask.id)}
          onDelete={() => {
            if (window.confirm(`Delete "${detailTask.title}"?`)) {
              deleteMut.mutate(detailTask.id);
            }
          }}
          onNavigateToMatter={(matterId) => navigate(`${basePath}/matters/${matterId}`)}
        />
      )}

      {/* Complete dialog */}
      {completeTarget && currentUser && (
        <CompleteTaskDialog
          task={completeTarget}
          completingUserName={`${currentUser.firstName} ${currentUser.lastName}`}
          isPending={completeMut.isPending}
          onClose={() => setCompleteTarget(null)}
          onConfirm={(note, file) =>
            completeMut.mutate({
              id: completeTarget.id,
              note,
              file,
              caseId: completeTarget.caseId ?? undefined,
            })
          }
        />
      )}

      {createOpen && (
        <TaskModal
          matterId={caseIdFromUrl}
          onClose={() => setCreateOpen(false)}
          onSaved={() => setCreateOpen(false)}
        />
      )}
      {editTarget && (
        <TaskModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}
